import { useCreateAssessmentMutation } from '@/app/redux/endpoints/assessments';
import {
  useCreateStudentMutation,
  useUpdateStudentMutation,
} from '@/app/redux/endpoints/students';
import { useAppSelector } from '@/app/redux/hooks';
import { useOrganisation } from '@/app/redux/hooks/organisation';
import { selectUserId } from '@/app/redux/slices/sessionSlice';
import { ASSESSMENT_VERSION } from '@/common/types/migrations/assessment';
import { ControllerType, Student } from '@/common/types/student';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import { getSeatLimitStatus } from '@/common/utils/license';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import {
  generateStudentPassword,
  initialGameSettings,
  initialStudent,
  isValidUsername,
  sanitizeUsername,
} from '@/common/utils/student';
import { showLearnerActionsTip } from '@/common/utils/tips';
import api from '@/db/apiClient';
import {
  CheckCircleIcon,
  CircleNotchIcon,
  EyeIcon,
  EyeSlashIcon,
  ShuffleIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { Alert } from '@shadcn/components/alert';
import { Button } from '@shadcn/components/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/dialog';
import { Input } from '@shadcn/components/input';
import { Label } from '@shadcn/components/label';
import { RadioGroup, RadioGroupItem } from '@shadcn/components/radio-group';
import { Separator } from '@shadcn/components/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { debounce } from 'lodash';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

interface StudentCreationModalProps {
  open: boolean;
  onClose: (studentId?: string) => void;
}
export default function StudentCreationModal({
  open,
  onClose,
}: StudentCreationModalProps) {
  const { organisation } = useOrganisation();

  const [createStudent] = useCreateStudentMutation();
  const [updateStudent] = useUpdateStudentMutation();
  const [createAssessment] = useCreateAssessmentMutation();

  const userId = useAppSelector(selectUserId);

  const [isLoading, setLoading] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState(generateStudentPassword());
  const [doScreener, setDoScreener] = useState(false);
  const [error, setError] = useState<string | JSX.Element | null>(null);
  const [usernameStatus, setUsernameStatus] = useState<boolean | undefined>();
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [isGeneratingUsername, setIsGeneratingUsername] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);

  const organisationId = organisation?.id;
  const orgDefaultSettings =
    organisation?.defaultStudentSettings ?? initialGameSettings;

  const seatLimitReached = useMemo(() => {
    const status = getSeatLimitStatus(
      organisation?.license,
      organisation?.studentCount,
    );
    return status === 'at-limit' || status === 'over-limit';
  }, [organisation]);

  const checkUsernameAvailability = useMemo(
    () =>
      debounce(async (username: string) => {
        if (!username) {
          setUsernameStatus(undefined);
          return;
        }
        setIsCheckingUsername(true);
        const isAvailable = await api.asEducator.isUsernameAvailable(username);
        setUsernameStatus(isAvailable);
        setIsCheckingUsername(false);
      }, 500),
    [],
  );

  async function handleAddLearner() {
    setError(null);

    if (!userId || !organisationId) {
      setError('Something went wrong. Please try again.');
      console.error('User or organisation ID missing');
      return;
    }

    if (!firstName.trim()) {
      setError('Please enter a first name');
      return;
    }

    if (!surname.trim()) {
      setError('Please enter a surname');
      return;
    }

    if (!username) {
      setError('Please enter a username');
      return;
    }

    if (!isValidUsername(username)) {
      setError(
        'Username must be 1-20 characters and only contain lowercase letters, numbers, and hyphens',
      );
      return;
    }

    if (!password) {
      setError('Please enter a password');
    }

    setLoading(true);
    const usernameAvailable =
      await api.asEducator.isUsernameAvailable(username);
    if (!usernameAvailable) {
      setError('Username is already taken. Please choose a different one.');
      setLoading(false);
      return;
    }

    try {
      const newStudent: Omit<Student, 'id'> = {
        ...initialStudent,
        studentFirstName: firstName.trim(),
        studentSurname: surname.trim(),
        spellcastUsername: username,
        password,
        createdBy: userId,
        organisationId: organisationId,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        settings: orgDefaultSettings,
      };

      // Create the student
      const student = await createStudent(newStudent).unwrap();

      trackMixpanelEvent('Student Created', {
        studentId: student.id,
      });

      if (doScreener) {
        const assessmentId = await handleCreateAssessment(student.id);
        await updateStudent({
          orgId: organisationId,
          studentId: student.id,
          updates: {
            assessmentId,
          },
        });
      }

      toast.success('Learner added');

      setTimeout(() => {
        showLearnerActionsTip();
      }, 500);

      onClose(student.id);
      setLoading(false);
      setFirstName('');
      setSurname('');
      setUsername('');
      setPassword(generateStudentPassword());
      setError(null);
    } catch (error) {
      console.error('Error creating and posting student:', error);
      setError(UNKNOWN_ERROR_MESSAGE);
    }
  }

  async function handleCreateAssessment(studentId: string) {
    if (!userId || !organisationId) {
      console.error('No user ID found');
      setError(UNKNOWN_ERROR_MESSAGE);
      return;
    }

    const now = Date.now();
    const newAssessment = await createAssessment({
      version: ASSESSMENT_VERSION,
      createdAt: now,
      updatedAt: now,
      createdBy: userId,
      organisationId,
      phase: 0,
      placeAtAssessedLevel: true,
      control: ControllerType.CLICK,
      doScreener: true,
      students: {
        [studentId]: {
          startedAt: null,
          completedAt: null,
          placementLevel: null,
          phase: 0,
          placeAtAssessedLevel: true,
          control: ControllerType.CLICK,
          doScreener,
        },
      },
      studentIds: [studentId],
    }).unwrap();
    return newAssessment.id;
  }

  async function handleGenerateUsername() {
    if (!firstName.trim() || !surname.trim()) return;
    try {
      setIsGeneratingUsername(true);
      const { usernames } = await api.asEducator.generateStudentUsernames({
        names: [{ firstName: firstName.trim(), lastName: surname.trim() }],
      });
      setUsername(usernames[0]);
      checkUsernameAvailability(usernames[0]);
    } catch (e) {
      console.error('Error generating username:', e);
      toast.error('Failed to generate username', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    } finally {
      setIsGeneratingUsername(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={() => {
        setError(null);
        setFirstName('');
        setSurname('');
        setUsername('');
        setPassword(generateStudentPassword());
        setPasswordVisible(false);
        setUsernameStatus(undefined);
        setDoScreener(false);
        onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Learner</DialogTitle>
          <DialogDescription>
            Add a learner to your organisation
          </DialogDescription>
        </DialogHeader>
        <div>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <WarningCircleIcon size={18} weight="bold" className="ml-1" />
              <span>{error}</span>
            </Alert>
          )}
          {seatLimitReached && (
            <Alert variant="warning" className="mb-4">
              <WarningCircleIcon size={18} weight="bold" className="ml-1" />
              <div>
                You have reached the maximum number of learner accounts for your
                plan. Go to your{' '}
                <a href="/dashboard/settings/billing" className="underline">
                  billing & subscription
                </a>{' '}
                page to add more learners.
              </div>
            </Alert>
          )}
          <div className="flex items-center gap-2 mb-2">
            <div className="flex flex-col gap-1 w-full">
              <Label html-for="firstName">First name</Label>
              <Input
                id="firstName"
                type="text"
                placeholder="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value.trimStart())}
              />
            </div>
            <div className="flex flex-col gap-1 w-full">
              <Label html-for="surname">Surname</Label>
              <Input
                id="surname"
                type="text"
                placeholder="Surname"
                value={surname}
                onChange={(e) => setSurname(e.target.value.trimStart())}
              />
            </div>
          </div>
          <Label html-for="username">Username</Label>

          <div className="relative">
            <Input
              id="username"
              className="mb-2"
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => {
                const newUsername = sanitizeUsername(e.target.value);
                setUsername(newUsername);
                checkUsernameAvailability(newUsername);
              }}
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={handleGenerateUsername}
                    loading={isGeneratingUsername}
                    disabled={!firstName.trim() || !surname.trim()}
                  >
                    {!isGeneratingUsername && (
                      <ShuffleIcon className="h-4 w-4" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Generate random username</TooltipContent>
              </Tooltip>
              {usernameStatus !== undefined && username && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center mr-1">
                      {isCheckingUsername ? (
                        <CircleNotchIcon
                          size={16}
                          className="text-muted-foreground animate-spin"
                        />
                      ) : usernameStatus ? (
                        <CheckCircleIcon size={16} className="text-green-500" />
                      ) : (
                        <WarningCircleIcon size={16} className="text-red-500" />
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {isCheckingUsername
                      ? 'Checking username...'
                      : usernameStatus
                        ? 'Available'
                        : 'Invalid or taken'}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>

          <Label html-for="password">Password</Label>
          <div className="relative">
            <Input
              id="password"
              type={passwordVisible ? 'text' : 'password'}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <div className="flex items-center gap-1 absolute right-1 top-1/2 -translate-y-1/2 bg-white">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setPassword(generateStudentPassword())}
                  >
                    <ShuffleIcon className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Generate random password</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setPasswordVisible((prev) => !prev)}
                  >
                    {passwordVisible ? (
                      <EyeSlashIcon className="size-4" />
                    ) : (
                      <EyeIcon className="size-4" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {passwordVisible ? 'Hide' : 'Show'} Password
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
          <div className="flex justify-end text-xs text-muted-foreground mt-1 mb-4">
            Tip: Click the checkbox next to the learner's name in the dashboard
            to export login details.
          </div>

          <Separator className="my-2" />

          <Label>Initial Placement Level</Label>
          <RadioGroup
            value={doScreener ? 'doScreener' : 'noScreener'}
            onValueChange={(value) => setDoScreener(value === 'doScreener')}
            className="flex flex-col gap-2"
          >
            <span className="text-muted-foreground text-xs">
              Choose how to set the learner's starting level
            </span>
            <label
              htmlFor="r1"
              className={`flex items-center gap-3 rounded-lg border py-2 px-3 cursor-pointer transition-colors ${
                doScreener ? 'border-primary bg-primary/5' : 'hover:bg-accent'
              }`}
            >
              <RadioGroupItem value="doScreener" id="r1" className="mt-0.5" />
              <div className="flex flex-col">
                <div className="font-normal text-sm">
                  Let us find their level
                </div>
                <span className="text-xs text-muted-foreground">
                  Learner will complete an assessment to automatically be placed
                  at the appropriate level.
                </span>
              </div>
            </label>

            <label
              htmlFor="r2"
              className={`flex items-center gap-3 rounded-lg border py-2 px-3 cursor-pointer transition-colors ${
                !doScreener ? 'border-primary bg-primary/5' : 'hover:bg-accent'
              }`}
            >
              <RadioGroupItem value="noScreener" id="r2" className="mt-0.5" />
              <div className="flex flex-col">
                <div className="font-normal text-sm">Set level manually</div>
                <span className="text-xs text-muted-foreground">
                  You can set the learner's level in the dashboard or create an
                  assessment later.
                </span>
              </div>
            </label>
          </RadioGroup>
        </div>

        <DialogFooter className="mt-1">
          <DialogClose asChild>
            <Button variant="outline" disabled={isLoading}>
              Cancel
            </Button>
          </DialogClose>
          <Button onClick={() => handleAddLearner()} loading={isLoading}>
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
